import React, { useState, useEffect } from "react";
import { Drawer, Form, Input, Button, Select, Upload, Row, Col, Steps, Typography, Popconfirm, InputNumber, Checkbox } from "antd";
import { LoadingOutlined, CheckCircleOutlined, PlusOutlined } from "@ant-design/icons";
import CompanyService from "../../services/CompanyService";
import "./Company.scss";
import { filterOption, openNotification, handlePhonePaste, toTurkishUpper } from "../../utils";
import { filterNotificationTypeOption, notificationTypeOptions, NotificationType } from "../../constants/notificationTypes";
import ImageUpload from "../Uploads/ImageUpload";
import { buildCompanyBillingPayload } from "./companyBilling";
// Map components for Control Address (match edit drawer)
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const validateOptionalTaxNumber = (_: any, value?: string) => {
    if (!value) {
        return Promise.resolve();
    }
    if (!/^[0-9]*$/.test(value)) {
        return Promise.reject(new Error('Vergi Numarası sadece rakamlardan oluşmalıdır'));
    }
    if (value.length < 10 || value.length > 11) {
        return Promise.reject(new Error('Vergi Numarası 10 veya 11 haneli olmalıdır'));
    }
    return Promise.resolve();
};

interface CompanyDrawerProps {
    visible: boolean;
    onClose: () => void;
}

const CompanyCreate: React.FC<CompanyDrawerProps> = ({ visible, onClose }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [form] = Form.useForm();
    const [imageId, setImageId] = useState<number | null>(null);
    const [cities, setCities] = useState<{ id: number, name: string }[]>([]);
    const [step1Data, setStep1Data] = useState<any>({});
    const [step2Data, setStep2Data] = useState<any>({});
    const [step3Data, setStep3Data] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const cityIdsToShow = [2170, 2195, 2150, 2167, 2151, 2176, 2163, 2218, 2175];
    const [districts, setDistricts] = useState<{ id: number; name: string }[]>([]);
    const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
    const [billingDistricts, setBillingDistricts] = useState<{ id: number; name: string }[]>([]);
    const [selectedBillingCityId, setSelectedBillingCityId] = useState<number | null>(null);

    const [controlDistricts, setControlDistricts] = useState<{ id: number; name: string }[]>([]);
    const [controlSelectedCityId, setControlSelectedCityId] = useState<number | null>(null);
    const hasSupply = Form.useWatch("hasSupply", form);
    const invoiceIssuedToDifferentOrganization = Form.useWatch("invoiceIssuedToDifferentOrganization", form);
    // Control Address map/location state
    const [controlLat, setControlLat] = useState<number | null>(null);
    const [controlLng, setControlLng] = useState<number | null>(null);
    const [leafletMap, setLeafletMap] = useState<any>(null);
    const DEFAULT_LAT = 41.0082; // Istanbul
    const DEFAULT_LNG = 28.9784;

    const defaultIcon = new L.Icon({
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
    });

    const { Title } = Typography;
    useEffect(() => {
        const getState = async () => {
            const response = await CompanyService.getStates(225);
            setCities(response.data);
        };
        getState();
    }, []);

    const next = () => {
        form.validateFields().then(values => {
            if (currentStep === 0) {
                const normalizedStep1Data = {
                    ...values,
                    name: toTurkishUpper(values.name),
                    taxOffice: toTurkishUpper(values.taxOffice),
                    address: toTurkishUpper(values.address),
                    billingCompanyName: toTurkishUpper(values.billingCompanyName),
                    billingAddress: toTurkishUpper(values.billingAddress),
                    logo: imageId,
                };
                setStep1Data(normalizedStep1Data);
                console.log("Step1Data:", normalizedStep1Data);
            } else if (currentStep === 1) {
                setStep2Data({
                    ...values,
                    controlAddressTitle: toTurkishUpper(values.controlAddressTitle),
                    controlAddress: toTurkishUpper(values.controlAddress),
                    controlAddressLine2: toTurkishUpper(values.controlAddressLine2),
                });
            }
            setCurrentStep(currentStep + 1);
        }).catch((info) => {
            console.log("Validation Failed:", info);
        });
        setTimeout(() => {
            console.log(step1Data);
        }, 1000);
    };


    const onFinish = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            const normalizedStep1Data = {
                ...step1Data,
                name: toTurkishUpper(step1Data.name),
                taxOffice: toTurkishUpper(step1Data.taxOffice),
                address: toTurkishUpper(step1Data.address),
                billingCompanyName: toTurkishUpper(step1Data.billingCompanyName),
                billingAddress: toTurkishUpper(step1Data.billingAddress),
            };
            const normalizedStep2Data = {
                ...step2Data,
                controlAddressTitle: toTurkishUpper(step2Data.controlAddressTitle),
                controlAddress: toTurkishUpper(step2Data.controlAddress),
                controlAddressLine2: toTurkishUpper(step2Data.controlAddressLine2),
            };
            const normalizedStep3Data = {
                ...values,
                authorizedPersonFirstName: toTurkishUpper(values.authorizedPersonFirstName),
                authorizedPersonLastName: toTurkishUpper(values.authorizedPersonLastName),
                authorizedPersonPosition: toTurkishUpper(values.authorizedPersonPosition),
            };

            setStep3Data(normalizedStep3Data);

            const billingPayload = buildCompanyBillingPayload({
                company: {
                    name: normalizedStep1Data.name,
                    taxOffice: normalizedStep1Data.taxOffice,
                    taxNumber: normalizedStep1Data.taxNumber,
                    email: normalizedStep1Data.email,
                    phoneNumber: normalizedStep1Data.phoneNumber,
                    address: normalizedStep1Data.address,
                    city: normalizedStep1Data.city,
                    district: normalizedStep1Data.district,
                },
                billing: {
                    invoiceIssuedToDifferentOrganization: normalizedStep1Data.invoiceIssuedToDifferentOrganization,
                    companyName: normalizedStep1Data.billingCompanyName,
                    email: normalizedStep1Data.billingEmail,
                    phoneNumber: normalizedStep1Data.billingPhoneNumber,
                    address: normalizedStep1Data.billingAddress,
                    city: normalizedStep1Data.billingCity,
                    district: normalizedStep1Data.billingDistrict,
                },
            });

            const companyData = {
                name: normalizedStep1Data.name,
                taxOffice: normalizedStep1Data.taxOffice,
                taxNumber: normalizedStep1Data.taxNumber,
                email: normalizedStep1Data.email.toLowerCase(),
                phoneNumber: normalizedStep1Data.phoneNumber,
                kepAddress: normalizedStep1Data.kepAddress,
                invoiceIssuedToDifferentOrganization: billingPayload.invoiceIssuedToDifferentOrganization,
                billingEmail: billingPayload.billingEmail,
                expenseIncluded: normalizedStep1Data.expenseIncluded ?? null,
                hasSupply: normalizedStep1Data.hasSupply ?? null,
                withholdingRate: normalizedStep1Data.hasSupply === true ? normalizedStep1Data.withholdingRate ?? null : null,
                withholdingCode: normalizedStep1Data.hasSupply === true ? normalizedStep1Data.withholdingCode ?? null : null,
                imageId: imageId,
                companyAddresses: [
                    billingPayload.companyAddress,
                    billingPayload.billingAddress,
                    {
                        addressLine1: normalizedStep2Data.controlAddress,
                        addressLine2: normalizedStep2Data.controlAddressLine2 || "",
                        city: normalizedStep2Data.controlCity,
                        state: normalizedStep2Data.controlDistrict,
                        postalCode: normalizedStep2Data.controlPostalCode || "",
                        country: "Turkey",
                        type: "CONTROL",
                        identityNumber: "",
                        phone: normalizedStep2Data.controlPhone || "",
                        taxNumber: "",
                        companyName: "",
                        taxOffice: "",
                        title: normalizedStep2Data.controlAddressTitle || "Kontrol Adresi 1",
                        fullName: "",
                        latitude: normalizedStep2Data.controlLatitude ?? controlLat ?? null,
                        longitude: normalizedStep2Data.controlLongitude ?? controlLng ?? null,
                        sgkNumber: normalizedStep2Data.controlSgkNumber || null,
                    }
                ],
                authorizedPersons: [
                    {
                        firstName: normalizedStep3Data.authorizedPersonFirstName,
                        lastName: normalizedStep3Data.authorizedPersonLastName,
                        position: normalizedStep3Data.authorizedPersonPosition,
                        email: normalizedStep3Data.authorizedPersonEmail.toLowerCase(),
                        phoneNumber: normalizedStep3Data.authorizedPersonPhoneNumber,
                        notificationTypes: normalizedStep3Data.authorizedPersonNotificationTypes as NotificationType[],
                    }
                ],
            };

            console.log(companyData); // Loglama işlemi

            await CompanyService.create(companyData);

            form.resetFields();
            setCurrentStep(0);
            openNotification('success', 'Başarılı', 'Firma Eklendi');
            onClose();
            setLoading(false);
        } catch (error) {
            console.error("Validation Failed:", error);
            openNotification('error', 'Hata', 'Firma Eklenemedi');
            setLoading(false);
        }
    };

    const steps = [
        {
            title: (
                <span className="text-start">
                    Adım 1 <br /> Firma Bilgileri
                </span>
            ),
            description: 'Tamamlandı',
            status: currentStep > 0 ? 'Tamamlandı' : 'Bekliyor',
        },
        {
            title: (
                <>
                    Adım 2 <br /> Kontrol Adresi
                </>
            ),
            description: 'Devam ediyor',
            status: currentStep > 1 ? 'Tamamlandı' : currentStep === 1 ? 'Devam ediyor' : 'Bekliyor',
        },
        {
            title: (
                <>
                    Adım 3 <br /> Yetkili Bilgileri
                </>
            ),
            description: 'Bekliyor',
            status: currentStep === 2 ? 'Devam ediyor' : 'Bekliyor',
        },
    ];



    const onStepChange = (nextStep: number) => {
        if (nextStep <= currentStep) {
            setCurrentStep(nextStep);
        }
    };



    const getDistricts = async (stateId: number) => {
        try {
            const response = await CompanyService.getDistricts(stateId);
            setDistricts(response.data);
            setControlDistricts(response.data);
        } catch (error) {
            console.error("İlçeler alınırken hata oluştu:", error);
        }
    };

    const handleCityChange = (value: number) => {
        const city = cities.find((c) => c.id === value);
        setSelectedCityId(value);
        form.setFieldsValue({ city: city?.name, district: undefined });
        setDistricts([]);
        if (value) {
            getDistricts(value);
        }
    };

    const handleDistrictChange = (value: string) => {
        // Sadece firma ilçesini güncelle, kontrol adresine kopyalama
        form.setFieldsValue({ district: value });
    };

    const handleBillingCityChange = async (value: number) => {
        const city = cities.find((c) => c.id === value);
        setSelectedBillingCityId(value);
        form.setFieldsValue({ billingCity: city?.name, billingDistrict: undefined });
        setBillingDistricts([]);
        if (!value) {
            return;
        }

        try {
            const response = await CompanyService.getDistricts(value);
            setBillingDistricts(response.data);
        } catch (error) {
            console.error("Fatura ilçeleri alınırken hata oluştu:", error);
        }
    };

    const handleBillingDistrictChange = (value: string) => {
        form.setFieldsValue({ billingDistrict: value });
    };

    const handleControlCityChange = (value: number) => {
        console.log(value);
        const city = cities.find((c) => c.id === value);
        setControlSelectedCityId(value);
        form.setFieldsValue({ controlCity: city?.name, controlDistrict: undefined });
        setControlDistricts([]);
        if (value) {
            getDistricts(value);
        }

    };

    const handleControlDistrictChange = (value: string) => {
        form.setFieldsValue({ controlDistrict: value });
    };

    // Parse location link or raw "lat,lng" and set controlLat/Lng + form fields
    const parseAndApplyControlLocation = (raw: string) => {
        if (!raw) return;
        let text = raw.trim();
        try {
            const url = new URL(text);
            const q = url.searchParams.get('q');
            if (q) text = q;
        } catch { }
        text = text.replace(/%2C/gi, ',');
        const m = text.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
        if (m) {
            const latNum = Number(m[1]);
            const lngNum = Number(m[2]);
            if (!Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
                setControlLat(latNum);
                setControlLng(lngNum);
                form.setFieldsValue({ controlLatitude: latNum, controlLongitude: lngNum });
            }
        }
    };

    return (
        <Drawer
            title="Yeni Firma Ekle"
            width={720}
            onClose={() => {
                setCurrentStep(0);
                onClose();
            }}
            visible={visible}
            bodyStyle={{ paddingBottom: 80 }}
            footer={
                <div className="text-right">
                    {currentStep < steps.length - 1 && (
                        <Button type="primary" onClick={next} loading={loading} className='d-button dark-button width-auto p-20px'>
                            Kaydet ve Devam Et
                        </Button>
                    )}
                    {currentStep === steps.length - 1 && (
                        <Button type="primary" onClick={onFinish} loading={loading} className='d-button dark-button width-auto p-20px'>
                            Kaydet ve Tamamla
                        </Button>
                    )}
                </div>
            }
        >
            <Row className="p-30px mb-16px border-bottom-1px" >
                <Steps
                    current={currentStep}
                    onChange={onStepChange}
                    items={steps.map((step, index) => ({
                        title: step.title,
                        description: step.status,
                        icon: index < currentStep ? <CheckCircleOutlined /> : null,
                    }))}
                    className="custom-steps"
                />
            </Row>
            <Form form={form} layout="vertical" >
                {currentStep === 0 && (
                    <>
                        <Title level={5}>Firma Bilgileri</Title>
                        <Row gutter={16}>
                            {/* <Col span={6}>
                                <Form.Item label="Logo" name="logo" valuePropName="file">
                                    <ImageUpload setImageId={setImageId} imageId={imageId} />
                                </Form.Item>
                            </Col> */}
                            <Col span={24}>
                                <Form.Item
                                    name="name"
                                    label="Firma Unvanı"
                                    className="form-item"
                                    rules={[{ required: true, message: 'Firma Unvanı boş bırakılamaz' }]}
                                >
                                    <Input
                                        placeholder="Firma Unvanı"
                                        className='d-input d-input-form'
                                        size="large"
                                        lang="tr"
                                        style={{ textTransform: "uppercase" }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                 
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="email"
                                    label="Firma E-posta Adresi"
                                    className="form-item"
                                    rules={[
                                        { required: true, message: 'E-posta boş bırakılamaz' },
                                        { type: 'email', message: 'Geçerli bir e-posta adresi girin' }
                                    ]}
                                >
                                    <Input
                                        placeholder="E-posta"
                                        className='d-input d-input-form '
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="phoneNumber"
                                    label="Firma Telefon Numarası"
                                    className="form-item"
                                    rules={[
                                        { required: true, message: 'Telefon Numarası boş bırakılamaz' },
                                        { pattern: /^[1-9]/, message: 'Telefon Numarası 0 ile başlamamalıdır' },
                                        { pattern: /^\d{10}$/, message: 'Telefon Numarası toplamda 10 haneli olmalıdır' },
                                    ]}
                                >
                                    <Input
                                        addonBefore="+90"
                                        placeholder="Telefon Numarası"
                                        className='d-input d-input-form'
                                        maxLength={10}
                                        size="large"
                                        onPaste={(e) => handlePhonePaste(e, { form, fieldName: 'phoneNumber' })}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="city"
                                    label="İl"
                                    className="form-item"
                                    rules={[{ required: true, message: "İl seçimi zorunludur" }]}
                                >
                                    <Select
                                        placeholder="İl seçin"
                                        className="d-input d-input-form"
                                        showSearch
                                        filterOption={filterOption}
                                        onChange={handleCityChange}
                                    >
                                        {cities.map((city) => (
                                            <Select.Option key={city.id} value={city.id}>
                                                {city.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="district"
                                    label="İlçe"
                                    rules={[{ required: true, message: "İlçe seçimi zorunludur" }]}
                                >
                                    <Select
                                        placeholder="İlçe seçin"
                                        className="d-input d-input-form"
                                        showSearch
                                        filterOption={filterOption}
                                        onChange={handleDistrictChange}
                                        disabled={!selectedCityId}
                                    >
                                        {districts.map((district) => (
                                            <Select.Option key={district.id} value={district.name}>
                                                {district.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="address"
                                    label="Fatura Adresi"
                                    className="form-item"
                                >
                                    <Input.TextArea
                                        placeholder="Fatura Adresi"
                                        className="d-input d-input-form"
                                        size="large"
                                        rows={4}
                                        lang="tr"
                                        style={{ textTransform: "uppercase" }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                               <Title level={5} className="mb-0">Faturalandırma</Title>
                        <Form.Item
                            name="invoiceIssuedToDifferentOrganization"
                            valuePropName="checked"
                            className="form-item"
                        >
                            <Checkbox>Fatura, farklı kişi/kuruluş adına düzenlensin</Checkbox>
                        </Form.Item>
                        <Typography.Text type="secondary" className="d-block mb-16px">
                            İşaretlenmediği takdirde fatura, hizmet sunulacak kuruluş bilgileri kullanılarak düzenlenecektir.
                        </Typography.Text>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="taxOffice"
                                    label="Vergi Dairesi"
                                    className="form-item"
                                >
                                    <Input
                                        placeholder="Vergi Dairesi"
                                        className='d-input d-input-form'
                                        size="large"
                                        lang="tr"
                                        style={{ textTransform: "uppercase" }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="taxNumber"
                                    label="Vergi Numarası"
                                    className="form-item"
                                    rules={[{ validator: validateOptionalTaxNumber }]}
                                >
                                    <Input placeholder="Vergi Numarası" className='d-input d-input-form' size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="kepAddress"
                                    label="KEP Adresi"
                                    className="form-item"
                                    rules={[
                                        { type: 'email', message: 'Geçerli bir KEP Adresi girin' }
                                    ]}
                                >
                                    <Input
                                        placeholder="KEP Adresi"
                                        className='d-input d-input-form'
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        {invoiceIssuedToDifferentOrganization ? (
                            <>
                                <Title level={5}>Fatura Bilgileri</Title>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item
                                            name="billingCompanyName"
                                            label="Firma Unvanı"
                                            className="form-item"
                                            rules={[{ required: true, message: 'Firma Unvanı boş bırakılamaz' }]}
                                        >
                                            <Input
                                                placeholder="Firma Unvanı"
                                                className='d-input d-input-form'
                                                size="large"
                                                lang="tr"
                                                style={{ textTransform: "uppercase" }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="billingPhoneNumber"
                                            label="Telefon Numarası"
                                            className="form-item"
                                            rules={[
                                                { required: true, message: 'Telefon Numarası boş bırakılamaz' },
                                                { pattern: /^[1-9]/, message: 'Telefon Numarası 0 ile başlamamalıdır' },
                                                { pattern: /^\d{10}$/, message: 'Telefon Numarası toplamda 10 haneli olmalıdır' },
                                            ]}
                                        >
                                            <Input
                                                addonBefore="+90"
                                                placeholder="Telefon Numarası"
                                                className='d-input d-input-form'
                                                maxLength={10}
                                                size="large"
                                                onPaste={(e) => handlePhonePaste(e, { form, fieldName: 'billingPhoneNumber' })}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="billingEmail"
                                            label="E-posta"
                                            className="form-item"
                                            rules={[
                                                { required: true, message: 'E-posta boş bırakılamaz' },
                                                { type: 'email', message: 'Geçerli bir e-posta adresi girin' }
                                            ]}
                                        >
                                            <Input
                                                placeholder="E-posta"
                                                className='d-input d-input-form'
                                                size="large"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="billingCity"
                                            label="İl"
                                            className="form-item"
                                            rules={[{ required: true, message: "İl seçimi zorunludur" }]}
                                        >
                                            <Select
                                                placeholder="İl seçin"
                                                className="d-input d-input-form"
                                                showSearch
                                                filterOption={filterOption}
                                                onChange={handleBillingCityChange}
                                            >
                                                {cities.map((city) => (
                                                    <Select.Option key={city.id} value={city.id}>
                                                        {city.name}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="billingDistrict"
                                            label="İlçe"
                                            rules={[{ required: true, message: "İlçe seçimi zorunludur" }]}
                                        >
                                            <Select
                                                placeholder="İlçe seçin"
                                                className="d-input d-input-form"
                                                showSearch
                                                filterOption={filterOption}
                                                onChange={handleBillingDistrictChange}
                                                disabled={!selectedBillingCityId}
                                            >
                                                {billingDistricts.map((district) => (
                                                    <Select.Option key={district.id} value={district.name}>
                                                        {district.name}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item
                                            name="billingAddress"
                                            label="Fatura Adresi"
                                            className="form-item"
                                        >
                                            <Input.TextArea
                                                placeholder="Fatura Adresi"
                                                className="d-input d-input-form"
                                                size="large"
                                                rows={4}
                                                lang="tr"
                                                style={{ textTransform: "uppercase" }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </>
                        ) : null}
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="expenseIncluded"
                                    label="Masraf Ekleniyor Mu?"
                                    className="form-item"
                                >
                                    <Select
                                        allowClear
                                        placeholder="Seçiniz"
                                        className="d-input d-input-form"
                                        size="large"
                                    >
                                        <Select.Option value={true}>EVET</Select.Option>
                                        <Select.Option value={false}>HAYIR</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="hasSupply"
                                    label="Tevkifat Var Mı?"
                                    className="form-item"
                                >
                                    <Select
                                        allowClear
                                        placeholder="Seçiniz"
                                        className="d-input d-input-form"
                                        size="large"
                                        onChange={(value) => {
                                            if (value !== true) {
                                                form.setFieldsValue({ withholdingRate: null, withholdingCode: null });
                                            }
                                        }}
                                    >
                                        <Select.Option value={true}>EVET</Select.Option>
                                        <Select.Option value={false}>HAYIR</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="withholdingRate"
                                    label="Tevkifat Oranı"
                                    className="form-item"
                                    rules={[
                                        { type: "number", min: 0, max: 100, message: "Tevkifat Oranı 0 ile 100 arasında olmalıdır" },
                                    ]}
                                >
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        precision={2}
                                        placeholder="Tevkifat Oranı"
                                        className="d-input d-input-form"
                                        size="large"
                                        style={{ width: "100%" }}
                                        disabled={hasSupply !== true}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="withholdingCode"
                                    label="Tevkifat Kodu"
                                    className="form-item"
                                >
                                    <Input.TextArea
                                        placeholder="Tevkifat Kodu"
                                        className="d-input d-input-form"
                                        rows={3}
                                        disabled={hasSupply !== true}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}
                {currentStep === 1 && (
                    <>
                        <Title level={5} className="mb-0">Kontrol Adresi</Title>
                        <p className="mt-0">Periyodik kontrolün gerçekleştirileceği adres bilgisini giriniz. <br />
                            Kontrolün gerçekleştirileceği birden fazla adres varsa, firma detay sayfasından diğer adresleri ekleyebilirsiniz.</p>
                        <Form.Item
                            name="controlAddressTitle"
                            label="Adres Başlığı"
                            className="form-item"
                            rules={[{ required: true, message: 'Adres Başlığı zorunludur' }]}
                        >
                            <Input
                                placeholder="Merkez, Üretim, Depo, Şube gibi"
                                className='d-input d-input-form'
                                size="large"
                                lang="tr"
                                style={{ textTransform: "uppercase" }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="controlAddress"
                            label="Adres"
                            className="form-item"
                            rules={[{ required: true, message: 'Adres boş bırakılamaz' }]}
                        >
                            <Input.TextArea
                                placeholder="Adres"
                                className='d-input d-input-form'
                                size="large"
                                lang="tr"
                                style={{ textTransform: "uppercase" }}
                            />
                        </Form.Item>
                        {/* <Form.Item
                            name="controlAddressLine2"
                            label="Adres (Devamı)"
                            className="form-item"
                        >
                            <Input
                                placeholder="Adres 2"
                                className='d-input d-input-form'
                                size="large"
                                lang="tr"
                                style={{ textTransform: "uppercase" }}
                            />
                        </Form.Item> */}

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="controlCity"
                                    label="İl"
                                    className="form-item"
                                    rules={[{ required: true, message: "İl seçimi zorunludur" }]}
                                >
                                    <Select
                                        placeholder="İl seçin"
                                        className="d-input d-input-form"
                                        showSearch
                                        filterOption={filterOption}
                                        onChange={handleControlCityChange}
                                    >
                                        {cities
                                            .filter((city) => cityIdsToShow.includes(city.id))
                                            .map((city) => (
                                                <Select.Option key={city.id} value={city.id}>
                                                    {city.name}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="controlDistrict"
                                    label="İlçe"
                                    rules={[{ required: true, message: "İlçe seçimi zorunludur" }]}
                                >
                                    <Select
                                        placeholder="İlçe seçin"
                                        className="d-input d-input-form"
                                        showSearch
                                        filterOption={filterOption}
                                        onChange={handleControlDistrictChange}
                                        disabled={!controlSelectedCityId}
                                    >
                                        {controlDistricts.map((district) => (
                                            <Select.Option key={district.id} value={district.name}>
                                                {district.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="controlPhone" label="Telefon Numarası" rules={[{ pattern: /^\d{10}$/, message: '10 haneli olmalıdır' }]}>
                                    <Input addonBefore="+90" maxLength={10} placeholder="Telefon Numarası" className='d-input d-input-form' size="large" onPaste={(e) => handlePhonePaste(e, { form, fieldName: 'controlPhone' })} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="controlSgkNumber"
                                    label="SGK/DETSİS Numarası"
                                    rules={[
                                        { pattern: /^[0-9]*$/, message: 'SGK/DETSİS Numarası sadece rakamlardan oluşmalıdır' },
                                        { min: 8, message: 'SGK/DETSİS Numarası en az 8 haneli olmalıdır' },
                                        { max: 26, message: 'SGK/DETSİS Numarası en fazla 26 haneli olmalıdır' }
                                    ]}
                                >
                                    <Input
                                        placeholder="SGK/DETSİS Numarası"
                                        maxLength={26}
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item name="controlLocationLink" label="Konum Linki (Google Maps)">
                                    <Input
                                        placeholder="https://maps.google.com/?q=41.070759,28.808908 veya 41.070759,28.808908"
                                        className='d-input d-input-form'
                                        size="large"
                                        onChange={(e) => parseAndApplyControlLocation(e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Typography.Text>Konum (Harita Üzerinden Seçiniz)</Typography.Text>
                                <div style={{ height: 320, borderRadius: 8, overflow: 'hidden', marginTop: 8, position: 'relative' }}>
                                    {/* Sil (koordinatları temizle) */}
                                    {(controlLat && controlLng) && (
                                        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1000 }}>
                                            <Popconfirm
                                                title="Emin misiniz?"
                                                okText="Evet"
                                                cancelText="Hayır"
                                                onConfirm={() => {
                                                    setControlLat(null);
                                                    setControlLng(null);
                                                    form.setFieldsValue({ controlLatitude: null, controlLongitude: null });
                                                }}
                                            >
                                                <Button danger size='small'>Seçimi kaldır</Button>
                                            </Popconfirm>
                                        </div>
                                    )}
                                    <MapContainer
                                        center={controlLat && controlLng ? [controlLat, controlLng] as any : [DEFAULT_LAT, DEFAULT_LNG]}
                                        zoom={controlLat && controlLng ? 12 : 8}
                                        style={{ height: '100%', width: '100%' }}
                                        whenCreated={(map: any) => setLeafletMap(map)}
                                    >
                                        <TileLayer
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        />
                                        {controlLat && controlLng && (
                                            <Marker position={[controlLat, controlLng] as any} icon={defaultIcon} />
                                        )}
                                        <MapClick
                                            onClick={(e: { lat: number; lng: number }) => {
                                                setControlLat(e.lat);
                                                setControlLng(e.lng);
                                                form.setFieldsValue({ controlLatitude: e.lat, controlLongitude: e.lng });
                                            }}
                                        />
                                    </MapContainer>
                                </div>
                                {controlLat && controlLng && (
                                    <div style={{ marginTop: 8 }}>
                                        <a href={`https://maps.google.com/?q=${controlLat},${controlLng}`} target="_blank" rel="noreferrer">Google Maps'te aç</a>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </>
                )}
                {currentStep === 2 && (
                    <>
                        <Title level={5} className="mb-0">Yetkili Bilgileri</Title>
                        <p className="mt-0">Firmaya ait yetkili bilgisini giriniz. <br />
                            Firmada birden fazla yetkili varsa, firma detay sayfasından diğer yetkilileri ekleyebilirsiniz.</p>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="authorizedPersonFirstName"
                                    label="Ad"
                                    className="form-item"
                                    rules={[{ required: true, message: 'Ad boş bırakılamaz' }]}
                                >
                                    <Input
                                        placeholder="Ad"
                                        className='d-input d-input-form'
                                        size="large"
                                        lang="tr"
                                        style={{ textTransform: "uppercase" }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="authorizedPersonLastName"
                                    label="Soyad"
                                    className="form-item"
                                    rules={[{ required: true, message: 'Soyad boş bırakılamaz' }]}
                                >
                                    <Input
                                        placeholder="Soyad"
                                        className='d-input d-input-form'
                                        size="large"
                                        lang="tr"
                                        style={{ textTransform: "uppercase" }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item
                            name="authorizedPersonPosition"
                            label="Unvan"
                            className="form-item"
                            rules={[]}
                        >
                            <Input
                                placeholder="Unvan"
                                className='d-input d-input-form'
                                size="large"
                                lang="tr"
                                style={{ textTransform: "uppercase" }}
                            />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="authorizedPersonEmail"
                                    label="E-posta"
                                    className="form-item "
                                    rules={[
                                        { required: true, message: 'E-posta boş bırakılamaz' },
                                        { type: 'email', message: 'Geçerli bir e-posta adresi girin' }
                                    ]}
                                >
                                    <Input
                                        placeholder="E-posta"
                                        className='d-input d-input-form '
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="authorizedPersonPhoneNumber"
                                    label="Telefon"
                                    className="form-item"
                                    rules={[
                                        { required: true, message: 'Telefon zorunludur' },
                                        { pattern: /^\d{10}$/, message: 'Telefon 10 haneli olmalı' },
                                    ]}
                                >
                                    <Input
                                        addonBefore="+90"
                                        placeholder="Telefon"
                                        className='d-input d-input-form'
                                        maxLength={10}
                                        size="large"
                                        onPaste={(e) => handlePhonePaste(e, { form, fieldName: 'authorizedPersonPhoneNumber' })}
                                    />
                                </Form.Item>

                            </Col>

                        </Row>
                        <Row>
                            <Col span={24}>
                                <Form.Item
                                    name="authorizedPersonNotificationTypes"
                                    label="İletişim Konuları"
                                    rules={[
                                        { required: true, type: 'array', min: 1, message: 'İletişim Konuları zorunludur' },
                                    ]}
                                >
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        placeholder="Yetki türü seçin"
                                        className="d-input d-input-form"
                                        options={notificationTypeOptions}
                                        size="large"
                                        optionFilterProp="label"
                                        filterOption={filterNotificationTypeOption}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}
            </Form>
        </Drawer>
    );
};

export default CompanyCreate;

// Helper: map click capture for leaflet
function MapClick({ onClick }: { onClick: (e: { lat: number; lng: number }) => void }) {
    useMapEvents({
        click(e: any) {
            onClick({ lat: e.latlng.lat, lng: e.latlng.lng });
        },
    });
    return null;
}
